import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Check, XCircle, Image as ImageIcon, Inbox, ExternalLink } from "lucide-react";
import { T } from "@/lib/testIds";

const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(n || 0));

export default function VerifyQueue() {
  const { user } = useAuth();
  const isAdmin = user?.role === "owner" || user?.role === "admin";
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [receiptBlob, setReceiptBlob] = useState(null);
  const [receiptTitle, setReceiptTitle] = useState("");

  const load = async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const r = await api.get("/payments", { params: { status: "pending_review", limit: 200 } });
      setPayments(r.data);
    } catch {
      toast.error("Failed to load queue");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [isAdmin]);

  if (!isAdmin) {
    return <Navigate to="/app/dashboard" replace />;
  }

  const viewReceipt = async (p) => {
    try {
      const r = await api.get(`/payments/${p.id}/receipt`, { responseType: "blob" });
      setReceiptBlob(URL.createObjectURL(r.data));
      setReceiptTitle(p.receipt_original_name || `Receipt · ${p.client_name || ""}`);
    } catch {
      toast.error("Could not load receipt");
    }
  };

  const closeReceipt = () => {
    if (receiptBlob) URL.revokeObjectURL(receiptBlob);
    setReceiptBlob(null);
  };

  const verify = async (p) => {
    setBusyId(p.id);
    try {
      await api.post(`/payments/${p.id}/verify`);
      toast.success(`Verified — ${p.client_name}`);
      setPayments((rs) => rs.filter((x) => x.id !== p.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verify failed");
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (p) => {
    if (!window.confirm(`Reject ${p.client_name}'s self-reported payment?`)) return;
    setBusyId(p.id);
    try {
      await api.post(`/payments/${p.id}/reject`);
      toast.success(`Rejected — ${p.client_name}`);
      setPayments((rs) => rs.filter((x) => x.id !== p.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <header className="co-glass-header sticky top-0 z-20 px-6 lg:px-10 py-5 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="co-label">Inbox</div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold tracking-tight mt-1 flex items-center gap-3">
            Payments to verify
            {payments.length > 0 && (
              <span className="co-pill co-pill-mock">{payments.length} pending</span>
            )}
          </h1>
        </div>
      </header>

      <div className="p-6 lg:p-10">
        <div className="border co-hairline bg-white overflow-x-auto">
          <table className="co-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Product</th>
                <th>Amount</th>
                <th>Reference</th>
                <th>Reported</th>
                <th>Receipt</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center text-[#52525B]">Loading…</td></tr>
              ) : payments.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <div className="py-14 flex flex-col items-center text-[#52525B]">
                      <Inbox className="h-8 w-8 text-[#4338CA]" strokeWidth={1.5} />
                      <div className="font-heading text-lg font-bold tracking-tight text-[#0A0A0B] mt-3">
                        Inbox zero
                      </div>
                      <div className="text-sm mt-1">
                        No self-reported payments waiting for verification.
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                payments.map((p) => (
                  <tr key={p.id} data-testid={T.verifyQueueRow}>
                    <td>
                      <Link
                        to={`/app/clients/${p.client_id}`}
                        className="font-semibold text-[#0A0A0B] hover:text-[#4338CA]"
                      >
                        {p.client_name || p.client_id.slice(0, 8)}
                      </Link>
                      {p.client_portal_token && (
                        <a
                          href={`/portal/${p.client_portal_token}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-2 inline-flex items-center gap-0.5 text-xs text-[#4338CA] hover:text-[#3730A3]"
                          title="Open client portal"
                        >
                          <ExternalLink className="h-3 w-3" strokeWidth={2} />
                        </a>
                      )}
                    </td>
                    <td className="text-[#52525B]">{p.client_product || "—"}</td>
                    <td className="font-mono">{fmtINR(p.amount)}</td>
                    <td className="text-xs font-mono max-w-[220px] truncate">{p.reference || "—"}</td>
                    <td className="text-xs font-mono">{new Date(p.created_at).toLocaleString()}</td>
                    <td>
                      {p.receipt_path ? (
                        <button
                          onClick={() => viewReceipt(p)}
                          data-testid={`${T.viewReceiptBtn}-${p.id}`}
                          className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 border border-[#0A0A0B] rounded-sm hover:bg-[#F4F4F5] co-btn-press transition-colors duration-200"
                        >
                          <ImageIcon className="h-3 w-3" strokeWidth={2} /> View
                        </button>
                      ) : (
                        <span className="text-xs text-[#52525B]">—</span>
                      )}
                    </td>
                    <td>
                      <div className="flex items-center justify-end gap-1">
                        <button
                          disabled={busyId === p.id}
                          onClick={() => verify(p)}
                          data-testid={`${T.verifyPaymentBtn}-${p.id}`}
                          className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 bg-[#15803D] hover:bg-[#166534] text-white rounded-sm co-btn-press disabled:opacity-60 transition-colors duration-200"
                        >
                          <Check className="h-3.5 w-3.5" strokeWidth={2} /> Verify
                        </button>
                        <button
                          disabled={busyId === p.id}
                          onClick={() => reject(p)}
                          data-testid={`${T.rejectPaymentBtn}-${p.id}`}
                          className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 border border-[#B91C1C] text-[#B91C1C] rounded-sm hover:bg-[#FEF2F2] co-btn-press disabled:opacity-60 transition-colors duration-200"
                        >
                          <XCircle className="h-3.5 w-3.5" strokeWidth={2} /> Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!receiptBlob} onOpenChange={(v) => { if (!v) closeReceipt(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl tracking-tight truncate">{receiptTitle}</DialogTitle>
          </DialogHeader>
          {receiptBlob && (
            <img
              src={receiptBlob}
              alt="Payment receipt"
              className="w-full max-h-[70vh] object-contain border co-hairline bg-[#FAFAFA]"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
